import clsx from 'clsx';
import { type CSSProperties, forwardRef } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { DOMProps } from '../../core/types/dom.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { textStyle } from '../../styles/textStyle.sty.js';
import { paragraph } from './Paragraph.sty.js';

const COMPONENT_NAME = 'Paragraph';

/**
 * The props for the Paragraph component.
 * @public
 *  */
export interface ParagraphProps
  extends
    WithChildren,
    DOMProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * Limits the text to the given number of lines and adds ellipsis if the text would need more lines.
   */
  maxLines?: number;
}

/**
 * The `Paragraph` component displays a block of text with the default text style and supports text truncation.
 * @public
 */
export const Paragraph = /* @__PURE__ */ forwardRef<
  HTMLParagraphElement,
  ParagraphProps
>((props, ref) => {
  const {
    children,
    maxLines,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  let ellipsis: undefined | 'singleLine' | 'multiLine';
  if (maxLines === undefined || maxLines === 0) {
    ellipsis = undefined;
  } else if (maxLines === 1) {
    ellipsis = 'singleLine';
  } else {
    ellipsis = 'multiLine';
  }

  return (
    <p
      ref={ref}
      className={clsx(paragraph({ ellipsis }), textStyle(), consumerClassName)}
      style={
        {
          ...consumerStyle,
          '--strato-ellipsis-line-clamp': maxLines,
        } as CSSProperties
      }
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {children}
    </p>
  );
});
(Paragraph as typeof Paragraph & { displayName: string }).displayName =
  COMPONENT_NAME;
